/**
 * NOTE: this file should not import any node modules, it needs to be just enum / type / interfaces
 * This file is shared with front-end
 */

import { User as AuthUser } from './user.js';


/**
 * Shared enum of Incident Report permission IDs.
 *
 * The IDs match the `permissionId` column in the database `RolePermissions` /
 * `UserPermissions` tables and are used when sending permission updates to the
 * server (see `PermissionUpdate.permissionId`).
 *
 * Member names intentionally mirror the keys of `IncidentReportPermissions` so
 * that a permission key can be converted to its numeric ID via a typed lookup,
 * e.g. `IncidentReportPermissionIds[key]`.
 *
 * IMPORTANT: This file MUST stay free of Node-only imports (e.g. `crypto`,
 * `mysql2`). The Angular front-end imports the enum directly, so any Node
 * dependency added here will break the client build.
 *
 * NOTE:
 *  - `incidentAccess` (45) and `onlyIncidentReports` (50) are used outside the
 *    Incident Management module to gate general access to incident features.
 *  - `incidentReportArchiver` and `incidentReportMessageEditor` are deprecated
 *    but kept for backwards compatibility.
 *
 * Declared as a regular `enum` (not `const enum`) so it exists at runtime and
 * supports dynamic lookups like `IncidentReportPermissionIds[key]`.
 */
export enum IncidentReportPermissionIds {
    incidentAccess = 45, // NOTE this permission is used outside Incident Management Module
    onlyIncidentReports = 50, // NOTE this permission is used outside Incident Management Module
    incidentReportViewOnly = 99,
    incidentReportAdmin = 100,
    incidentReportManageUser = 109,
    incidentReportArchiver = 106, // DEPRECATED - Gary left just in case
    incidentReportManager = 101,
    incidentReportMessageEditor = 107, // DEPRECATED - Gary left just in case
    incidentReportReviewer1 = 104,
    incidentReportReviewer2 = 105,
    incidentReportSubmitter = 103,
    incidentReportTraumaViewer = 108,
    incidentReportUser = 102
}

export interface IncidentReportPermissions {
    incidentAccess: boolean;
    onlyIncidentReports: boolean;
    incidentReportAdmin: boolean;
    incidentReportManageUser: boolean;
    incidentReportArchiver: boolean; // DEPRECATED - Gary left just in case
    incidentReportManager: boolean;
    incidentReportMessageEditor: boolean; // DEPRECATED - Gary left just in case
    incidentReportReviewer1: boolean;
    incidentReportReviewer2: boolean;
    incidentReportSubmitter: boolean;
    incidentReportTraumaViewer: boolean;
    incidentReportUser: boolean;
    incidentReportViewOnly: boolean;
}

export enum IncidentPermission {
    Admin = 'incidentReportAdmin',
    ManageUser = 'incidentReportManageUser',
    Manager = 'incidentReportManager',
    /** aka incidentReportUser */
    User = 'incidentReportUser',
    Submitter = 'incidentReportSubmitter',
    Reviewer1 = 'incidentReportReviewer1',
    Reviewer2 = 'incidentReportReviewer2',
    Archiver = 'incidentReportArchiver', // DEPRECATED - Gary left just in case
    MessageEditor = 'incidentReportMessageEditor', // DEPRECATED - Gary left just in case
    TraumaViewer = 'incidentReportTraumaViewer',
    ViewOnly = 'incidentReportViewOnly'
}

export const IncidentPermissionKeys = Object.values(IncidentPermission) as string[];

export const IR_ROLE_TO_PERMISSION = {
    admin: IncidentPermission.Admin,
    manager: IncidentPermission.Manager,
    manageUser: IncidentPermission.ManageUser,
    user: IncidentPermission.User,
    submitter: IncidentPermission.Submitter,
    reviewer1: IncidentPermission.Reviewer1,
    reviewer2: IncidentPermission.Reviewer2,
    archiver: IncidentPermission.Archiver,
    messageEditor: IncidentPermission.MessageEditor,
    traumaViewer: IncidentPermission.TraumaViewer,
    viewOnly: IncidentPermission.ViewOnly
} as const;

export type IncidentRole = keyof typeof IR_ROLE_TO_PERMISSION;

export interface MetricsPermissions {
    /**
     * "Create System Metrics". Add and maintain this library's own Performance
     * Indicators on Metrics Mapping: the page itself, Export to CSV, mapping
     * configuration, the custom field lifecycle and group management.
     *
     * Granted automatically to Library Admins through RolePermissions, and
     * grantable to individual users by a Library Admin or by IQ Staff.
     * Equivalent to `metricsMapping` for access — see
     * canConfigureMetricsMapping(), which is the only thing that should decide
     * the two together.
     *
     * It does NOT grant Editable, reconfiguring IQ Managed rows, or editing
     * the shared Data Dictionary.
     */
    createSystemMetrics: boolean;
    metricsAdmin: boolean;
    metricsCMTEnter: boolean;
    /** Enter performance statistics — monthly and daily alike. */
    metricsEnter: boolean;
    metricsIQEnter: boolean;
    metricsLock: boolean;
    metricsMapping: boolean;
    metricsUpload: boolean;
}

export type VendorAccess = 'all' | 'brodart' | 'ingram';

export interface NotifyManagerPermissions {
    notifyManagerAdmin: boolean;
    notifyManagerEditor: boolean;
    notifyManagerViewer: boolean;
}

/** Scheduler module (Setup & Admin, Create Schedule, published Schedule). */
export interface SchedulerPermissions {
    /**
     * Configure library hours, tasks, staff, rotations and time off. Reads are
     * open to anyone with library access; every write in the Scheduler module
     * is gated on this.
     */
    schedulerAdmin: boolean;
}

/** Funding Intelligence module (grant catalog, library grant profile, matches). */
export interface FundingPermissions {
    /**
     * Maintain the shared grant catalog: sources, programs, opportunities,
     * eligibility rules, CSV import and verification. The catalog is global
     * (not per library), so this is granted to Platform Staff by role and to
     * individual users only deliberately. Library-facing reads and the
     * library's own profile/tracking need only library access.
     */
    fundingAdmin: boolean;
}

export interface BibzAccount {
    userName: string;
    password: string;
}

// Top-level — composed from groups + the truly miscellaneous ones
export interface AllUserPermissions
    extends IncidentReportPermissions,
            MetricsPermissions,
            NotifyManagerPermissions,
            SchedulerPermissions,
            FundingPermissions {
    accessAdminIntegrations: boolean;
    accessMetis: boolean;
    accessMetisToolCalls: boolean;
    addCustomReports: boolean;
    /** @deprecated */
    balanceAdmin: boolean;
    bibzAccess: boolean;
    bibzAccount: BibzAccount[];
    branchAdmin: boolean;
    branchLibrary: number[];
    createLibraryTemplates: boolean;
    createReportTemplates: boolean;
    deleteUsers: boolean;
    demoData: string;
    displayPopUp: boolean;
    editKeepSettings: boolean;
    favoritePages: string[];
    inventoryModule: boolean;
    libraryBranchSetup: boolean;
    librarySystemSettings: boolean;
    manageFeatureFlags: boolean;
    manageFeatureSpotlight: boolean;
    mfaEnabled: boolean;
    patronAndCommunity: boolean;
    shareWithSmartyCat: boolean;
    userManagement: number;
    clearReportCacheByLibrary: boolean;
    runKPIReport: boolean;
    userPermissionsAuditReport: boolean;
    vendorAccess: VendorAccess;
    seriesAccess: boolean;
}

/** Resulting type: 'vendorAccess' | 'userManagement' etc */
export type UserPermission = keyof AllUserPermissions;

/**
 * 
    Functions below are intended to be used outside a service as to avoid circular deps
 */

/**
 * Check if user has a specific incident reporting permission
 */
export const hasIncidentPermission = (permission: IncidentPermission, user: AuthUser): boolean => {
    const perms = user?.permissions;
    if (!perms){
        return false;
    }
    return user.permissions[permission];
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyIncidentPermission = (permissions: IncidentPermission[], user: AuthUser): boolean => {
    const perms = user?.permissions;
    if (!perms){
        return false;
    }
    return permissions.some((permission) => hasIncidentPermission(permission, user));
};

export const hasAnyIncidentViewPermission = (user: AuthUser): boolean => {
    const perms = user?.permissions;
    if (!perms){
        return false;
    }
    return hasAnyIncidentPermission([
        IncidentPermission.User,
        IncidentPermission.Reviewer1,
        IncidentPermission.Reviewer2,
        IncidentPermission.Archiver,
        IncidentPermission.MessageEditor,
        IncidentPermission.ViewOnly
    ], user);
};

export const hasIncidentAdminOrManager = (user: AuthUser): boolean => {
    const perms = user?.permissions;
    if (!perms){
        return false;
    }
    return hasAnyIncidentPermission([
            IncidentPermission.Admin,
            IncidentPermission.Manager
    ], user);
};

/**
 * Check if user has basic incident access
 * Returns true if user has incidentAccess flag OR any incidentReport* role permission
 */
export const hasIncidentAccess = (user: AuthUser): boolean => {
    const perms = user?.permissions;
    if (!perms){
        return false;
    }

    // Allow access if user has incidentAccess flag OR any incidentReport* role
    return (
        perms.incidentAccess ||
        perms.onlyIncidentReports ||
        hasAnyIncidentPermission(
            [
                IncidentPermission.Admin,
                IncidentPermission.ManageUser,
                IncidentPermission.Manager,
                IncidentPermission.User,
                IncidentPermission.Submitter,
                IncidentPermission.Reviewer1,
                IncidentPermission.Reviewer2,
                IncidentPermission.Archiver,
                IncidentPermission.MessageEditor,
                IncidentPermission.TraumaViewer,
                IncidentPermission.ViewOnly
            ],
            user
        )
    );
};

/**
 * Check if user is Submitter-only (no higher permissions)
 * Submitters can only see their own drafts, not submitted reports
 * Must match server-side logic in server/routes/incident/reports/lists.ts
 */
export const isIncidentSubmitterOnly = (user: AuthUser): boolean => {
    return hasIncidentPermission(IncidentPermission.Submitter, user) &&
        !hasAnyIncidentPermission(
            [
                IncidentPermission.Admin,
                IncidentPermission.ManageUser,
                IncidentPermission.Manager,
                IncidentPermission.User,
                IncidentPermission.Reviewer1,
                IncidentPermission.Reviewer2,
                IncidentPermission.Archiver,
                IncidentPermission.MessageEditor,
                IncidentPermission.TraumaViewer,
                IncidentPermission.ViewOnly
            ],
            user
        );
};

export const isIncidentViewOnly = (user: AuthUser): boolean => {
    return hasIncidentPermission(IncidentPermission.ViewOnly, user) &&
        !hasAnyIncidentPermission(
            [
                IncidentPermission.Admin,
                IncidentPermission.ManageUser,
                IncidentPermission.Manager,
                IncidentPermission.User,
                IncidentPermission.Reviewer1,
                IncidentPermission.Reviewer2,
                IncidentPermission.Archiver,
                IncidentPermission.MessageEditor,
                IncidentPermission.TraumaViewer,
                IncidentPermission.Submitter
            ],
            user
        );
};

